import { useEffect, useState } from "react";
import { Star, Clock4, Sparkles, ShieldCheck, Award, Activity } from "lucide-react";
import api from "../lib/api";

function StatBlock({ icon: Icon, label, value, sub, accent = "text-blue-300", testId }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.18em]">
        <Icon className={`h-3.5 w-3.5 ${accent}`} /> {label}
      </div>
      <div className="mt-3 text-4xl font-extrabold stat-num leading-none">{value}</div>
      {sub && <div className="mt-2 text-xs text-white/45">{sub}</div>}
    </div>
  );
}

function EngineerCard({ rank, name, specialization, consults, avg_rating }) {
  const initials = (name || "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const rankBadge =
    rank === 1 ? "from-amber-300 to-amber-500" : rank === 2 ? "from-zinc-200 to-zinc-400" : "from-orange-300 to-orange-500";
  return (
    <div
      className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#111827] to-[#0F172A] p-5 hover:border-blue-500/40 transition-all"
      data-testid={`leaderboard-engineer-${rank}`}
    >
      <div className={`absolute -top-3 left-5 grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br ${rankBadge} text-[#0A101D] text-xs font-extrabold shadow-lg`}>
        #{rank}
      </div>
      <div className="flex items-center gap-3 mt-2">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-blue-500/30 to-cyan-400/30 border border-white/10 text-white font-bold tracking-wide">
          {initials || <Award className="h-5 w-5" />}
        </div>
        <div className="min-w-0">
          <div className="font-semibold truncate">{name}</div>
          <div className="text-xs text-white/50 truncate">{specialization}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-center">
        <div className="rounded-lg border border-white/5 bg-white/[0.03] py-2.5">
          <div className="flex items-center justify-center gap-1 text-amber-300 text-sm font-bold">
            <Star className="h-3.5 w-3.5 fill-amber-300" /> {avg_rating?.toFixed?.(2) || avg_rating}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-white/45 mt-1">Avg rating</div>
        </div>
        <div className="rounded-lg border border-white/5 bg-white/[0.03] py-2.5">
          <div className="text-sm font-bold">{consults}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/45 mt-1">Consults</div>
        </div>
      </div>
    </div>
  );
}

export default function LeaderboardSection() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, []);

  // Don't render at all if we have absolutely no rated data — keeps the homepage honest.
  if (stats && stats.rated_count === 0 && (stats.top_engineers?.length ?? 0) === 0) {
    return null;
  }

  const display = stats || { avg_rating: "—", total_consults: "—", last_30d_consults: "—", avg_resolution_minutes: "—", five_star_percentage: "—", top_engineers: [] };

  return (
    <section className="section-pad border-t border-white/5 bg-gradient-to-b from-[#0B1220] to-[#0A101D]" data-testid="leaderboard-section">
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <span className="eyebrow">Live trust signals</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              The numbers behind our<br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-amber-300 via-blue-300 to-cyan-300 bg-clip-text text-transparent">money-back guarantee.</span>
            </h2>
          </div>
          <p className="max-w-sm text-white/60">
            Aggregated from every paid video consultation we&apos;ve handled — refreshed in real time. If we can&apos;t resolve your issue, the refund is automatic.
          </p>
        </div>

        {/* Top-line stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatBlock
            icon={Star}
            label="Avg customer rating"
            value={typeof display.avg_rating === "number" ? `${display.avg_rating.toFixed(2)} ★` : `${display.avg_rating}`}
            sub={`Across ${stats?.rated_count ?? 0} rated consultations`}
            accent="text-amber-300"
            testId="leaderboard-stat-avg-rating"
          />
          <StatBlock
            icon={Sparkles}
            label="5-star resolutions"
            value={`${display.five_star_percentage}%`}
            sub="Customers who marked their issue solved"
            accent="text-cyan-300"
            testId="leaderboard-stat-fivestar"
          />
          <StatBlock
            icon={Clock4}
            label="Avg resolution time"
            value={(() => {
              const m = display.avg_resolution_minutes;
              if (typeof m !== "number" || m <= 0) return "—";
              if (m < 1) return `${Math.max(1, Math.round(m * 60))} sec`;
              if (m < 60) return `${m.toFixed(0)} min`;
              return `${(m / 60).toFixed(1)} hr`;
            })()}
            sub="From payment to resolution"
            accent="text-emerald-300"
            testId="leaderboard-stat-resolution"
          />
          <StatBlock
            icon={Activity}
            label="Last 30-day consults"
            value={display.last_30d_consults}
            sub={`${display.total_consults} all-time`}
            accent="text-blue-300"
            testId="leaderboard-stat-30d"
          />
        </div>

        {/* Top engineers grid */}
        {Array.isArray(display.top_engineers) && display.top_engineers.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-5">
              <Award className="h-5 w-5 text-amber-300" />
              <h3 className="text-xl font-bold">Engineers of the month</h3>
              <span className="ml-2 text-xs text-white/45">Top performers by paid consultations + customer rating</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {display.top_engineers.map((eng, i) => (
                <EngineerCard key={eng.name + i} rank={i + 1} {...eng} />
              ))}
            </div>
          </div>
        )}

        {/* Money-back footer line */}
        <div className="mt-12 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-emerald-500/15 border border-emerald-500/30">
            <ShieldCheck className="h-5 w-5 text-emerald-300" />
          </div>
          <div className="flex-1">
            <div className="font-bold">100% money-back if we don&apos;t fix it.</div>
            <p className="text-sm text-white/60 mt-0.5">Rate your video consult right after the call. Mark it &quot;not resolved&quot; and your ₹200 is refunded automatically — no questions asked.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
